/**
 * Therapist Chat API Service
 *
 * API client functions for freeform therapist chat threads.
 * Includes streaming support via Server-Sent Events (SSE) for real-time responses.
 *
 * Key responsibilities:
 * - Create/list/get/update/delete therapist chat threads
 * - Stream AI responses token-by-token via SSE
 * - Handle authentication and error recovery
 */

import { apiClient } from "./api";
import type {
  TherapistThreadCreate,
  TherapistThreadResponse,
  TherapistThreadListResponse,
  TherapistMessageCreate,
  TherapistStreamEvent,
} from "@/types/therapistChat";

const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8101";
const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Get access token for authenticated requests
 */
let getAccessToken: (() => Promise<string>) | null = null;

export function setTherapistChatTokenGetter(getter: () => Promise<string>) {
  getAccessToken = getter;
}

/**
 * Create a new therapist chat thread
 */
export async function createThread(
  data: TherapistThreadCreate
): Promise<TherapistThreadResponse> {
  const response = await apiClient.post(
    `${API_PREFIX}/therapist/threads`,
    data
  );
  return response.data;
}

/**
 * List therapist chat threads for the current user
 */
export async function listThreads(
  limit = 20,
  offset = 0
): Promise<TherapistThreadListResponse> {
  const response = await apiClient.get(`${API_PREFIX}/therapist/threads`, {
    params: { limit, offset },
  });
  return response.data;
}

/**
 * Get a single therapist chat thread with messages
 */
export async function getThread(
  threadId: string
): Promise<TherapistThreadResponse> {
  const response = await apiClient.get(
    `${API_PREFIX}/therapist/threads/${threadId}`
  );
  return response.data;
}

/**
 * Update a thread's title
 */
export async function updateThreadTitle(
  threadId: string,
  title: string
): Promise<TherapistThreadResponse> {
  const response = await apiClient.patch(
    `${API_PREFIX}/therapist/threads/${threadId}`,
    { title }
  );
  return response.data;
}

/**
 * Delete a therapist chat thread and all its messages
 */
export async function deleteThread(threadId: string): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/therapist/threads/${threadId}`);
}

/**
 * Send a message and receive AI response via Server-Sent Events
 *
 * Opens an SSE connection and invokes the onEvent callback for each event.
 * Supports cancellation via an optional AbortSignal.
 *
 * @param threadId - ID of the thread
 * @param message - User message to send
 * @param onEvent - Callback invoked for each streaming event
 * @param signal - Optional AbortSignal for cancellation
 */
export async function sendMessageStreaming(
  threadId: string,
  message: TherapistMessageCreate,
  onEvent: (event: TherapistStreamEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  // Get auth token
  let token: string | null = null;
  if (getAccessToken) {
    try {
      token = await getAccessToken();
    } catch {
      throw new Error("Failed to get authentication token");
    }
  }

  // Build URL
  const url = `${API_BASE_URL}${API_PREFIX}/therapist/threads/${threadId}/messages/stream`;

  // Send request with fetch for streaming support
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(message),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Streaming request failed: ${response.status} ${errorText}`
    );
  }

  if (!response.body) {
    throw new Error("Response body is null");
  }

  // Parse SSE stream
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let streamComplete = false;

  try {
    while (!streamComplete) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      // Decode and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Keep incomplete line in buffer

      for (const line of lines) {
        // SSE format: "data: {json}\n\n"
        if (!line.startsWith("data: ")) continue;
        const data = line.slice(6); // Remove "data: " prefix

        let event: TherapistStreamEvent;
        /*
          Only JSON.parse belongs in this try. It used to wrap the onEvent
          dispatch as well, and the consumer signals stream failure by throwing
          from that callback — so a server `error` event was caught here, logged
          as a parse failure, and discarded. The stream then resolved as if it
          had succeeded, the caller cleared its state, and the user was left
          looking at their own message with no reply, no error, and no spinner.
        */
        try {
          event = JSON.parse(data) as TherapistStreamEvent;
        } catch (parseError) {
          console.error("Failed to parse SSE event:", parseError, data);
          continue;
        }

        onEvent(event);

        /*
          The stream owns the error contract rather than relying on the
          consumer's callback to throw. The backend reports a turn that
          produced no text this way — a real, user-facing message about the
          question being too broad — and it has to reach the caller.
        */
        if (event.type === "error") {
          throw new Error(event.error || "Streaming error");
        }

        if (event.type === "done") {
          streamComplete = true;
          break;
        }
      }
    }

    /*
      Reaching here without a `done` event means the connection dropped
      mid-answer. Returning normally would again present a silent failure as
      success, so it is reported as what it is. An abort is the user's own
      cancellation and is left to the caller's AbortError handling.
    */
    if (!streamComplete && !signal?.aborted) {
      throw new Error(
        "The connection closed before the response finished. Please try again."
      );
    }
  } finally {
    reader.releaseLock();
  }
}
